import { z } from 'zod';

import { PaginationResponseSchema } from '@/shared/global.schema';

export const InventoryProductItemForSale = z.object({
    product_variation_id: z.string(),
    product_id: z.string(),
    name: z.string(),
    strength: z.string(),
    type: z.string(),
    unit: z.string(),
    purchase_id: z.string(),
    quantity: z.number(),
    expiration_date: z.string(),
    purchase_price: z.number(),
    wholesale_price: z.number(),
    retail_price: z.number(),
    product_name: z.string(),
    brand: z.string().optional(),
    brand_id: z.string().optional(),
});
const BatchItem = z.object({
    expiration_date: z.string(),
    purchase_price: z.number(),
    wholesale_price: z.number(),
    retail_price: z.number(),
    purchase_id: z.string(),
    quantity: z.number(),
});
export const InventoryProductItemsForStock = z.object({
    product_variation_id: z.string(),
    product_id: z.string(),
    total_quantity: z.number(),
    product_name: z.string(),
    brand_name: z.string(),
    supplier: z.string(),
    batches: z.array(BatchItem),
    status: z.string(),
    purchase_price: z.number(),
    wholesale_price: z.number(),
    retail_price: z.number(),
});
export const InventoryProductListForSale = z.object({
    data: z.array(InventoryProductItemForSale),
    message: z.string(),
});

export const CartStockItemSchema = z.object({
    product_variation_id: z.string(),
    product_id: z.string(),
    name: z.string(),
    strength: z.string(),
    type: z.string(),
    unit: z.string(),
    purchase_item_id: z.string(),
    quantity: z.number(),
    expiration_date: z.string(),
    purchase_price: z.number(),
    wholesale_price: z.number(),
    retail_price: z.number(),
    brand: z.string().optional(),
    brand_id: z.string().optional(),
});

export const CartStocksSchema = z.object({
    data: z.array(CartStockItemSchema),
    message: z.string(),
});

export const InventoryProductListForStock = z.object({
    data: z.array(InventoryProductItemsForStock),
    message: z.string(),
    pagination: PaginationResponseSchema,
});

// Inventory product Item for stock
export type InventoryProductItemsForStock = z.infer<
    typeof InventoryProductItemsForStock
>;
// Inventory product list for stock
export type InventoryProductListForStock = z.infer<
    typeof InventoryProductListForStock
>;
// Inventory product Item for stock
export type InventoryProductItemsForSale = z.infer<
    typeof InventoryProductItemForSale
>;
// Inventory product list for stock
export type InventoryProductListForSale = z.infer<
    typeof InventoryProductListForSale
>;

export type Batch = z.infer<typeof BatchItem>;

export const StockItemSchema = z.object({
    product_variation_id: z.string(),
    name: z.string(),
    brand_id: z.string().nullable(),
    brand_name: z.string().nullable(),
    generic_id: z.string().nullable(),
    generic_name: z.string().nullable(),
    alert_quantity: z.number(),
    latest_purchase_date: z.string().nullable(),
    latest_expiration_date: z.string().nullable(),
    latest_purchase_price: z.number().nullable(),
    latest_wholesale_price: z.number().nullable(),
    latest_retail_price: z.number().nullable(),
    current_stock: z.number(),
    expired_stock: z.coerce.number().optional(),
});

export type TStockItem = z.infer<typeof StockItemSchema>;

export const StocksResponseSchema = z.object({
    data: z.array(StockItemSchema),
    pagination: PaginationResponseSchema,
});

export type TStocksResponse = z.infer<typeof StocksResponseSchema>;

export const StockAdjustmentListItemSchema = z.object({
    id: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    product_variation_id: z.string(),
    user_id: z.string(),
    purchase_item_id: z.string(),
    quantity: z.number(),
    brand_id: z.string().nullable(),
    generic_id: z.string().nullable(),
    brand_name: z.string().nullable(),
    generic_name: z.string().nullable(),
    product_name: z.string(),
    note: z.string().nullable().optional(),
});

export type TStockAdjustmentListItem = z.infer<
    typeof StockAdjustmentListItemSchema
>;

export const StockAdjustmentListResponseSchema = z.object({
    data: z.array(StockAdjustmentListItemSchema),
    pagination: PaginationResponseSchema,
});

export type TStockAdjustmentListResponse = z.infer<
    typeof StockAdjustmentListResponseSchema
>;

export const LowStockItemSchema = z.object({
    current_stock: z.coerce.number(),
    product_variation_id: z.string(),
    product_id: z.string(),
    alert_quantity: z.number(),
    purchase_price: z.number(),
    wholesale_price: z.number(),
    retail_price: z.number(),
    brand_id: z.string().nullable(),
    generic_id: z.string().nullable(),
    brand_name: z.string().nullable(),
    generic_name: z.string().nullable(),
    product_name: z.string(),
});

export type TLowStockItem = z.infer<typeof LowStockItemSchema>;

export const LowStockListResponseSchema = z.object({
    data: z.array(LowStockItemSchema),
    pagination: PaginationResponseSchema,
});

export type TLowStockListResponse = z.infer<typeof LowStockListResponseSchema>;

export const ProductStockDetailsSchema = z.object({
    id: z.string(),
    created_at: z.string(),
    updated_at: z.string().nullable(),
    product_id: z.string(),
    alert_quantity: z.number(),
    purchase_price: z.number(),
    wholesale_price: z.number(),
    retail_price: z.number(),
    brand_id: z.string().nullable(),
    generic_id: z.string().nullable(),
    brand_name: z.string().nullable(),
    generic_name: z.string().nullable(),
    strength_name: z.string().nullable(),
    type_name: z.string().nullable(),
    name: z.string(),
    current_stock: z.number(),
    current_batches: z.number(),
    nearest_expiration: z.string().nullable(),
    batches: z
        .array(
            z.object({
                purchase_quantity: z.number(),
                expiration_date: z.string(),
                purchase_price: z.number(),
                wholesale_price: z.number(),
                retail_price: z.number(),
                current_stock: z.number(),
            }),
        )
        .nullable(),
});
export type TProductStockDetails = z.infer<typeof ProductStockDetailsSchema>;

export const ProductStockDetailsResponseSchema = z.object({
    data: ProductStockDetailsSchema,
});

export type TProductStockDetailsResponse = z.infer<
    typeof ProductStockDetailsResponseSchema
>;
